import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import type { CurrentUser } from '@wundo/client/auth.ts';
import { fetchImpersonatedBy } from '@wundo/client/impersonation.ts';
import { customerAppUrl } from './config.ts';

/**
 * The gate on the whole console, run after the session guard.
 *
 * This console is staff tooling — `docs/design/frontends.md` puts customers on
 * the customer app and states that no customer screens remain on this domain.
 * Being signed in is therefore not enough to be here: a customer with a valid
 * session is on the wrong domain, not missing a permission, so they are sent to
 * the app they belong on rather than shown a refusal.
 *
 * Impersonating is the one way a customer's session belongs here. The
 * superadmin who started it is still in the console they started it from, and
 * sending them away would take with them the only screen that offers a way back
 * — the tab they left open would bounce itself out the moment they looked at it
 * again. There is nothing to see behind it either way: the banner says whose
 * session this is, and stopping is one click from every page.
 *
 * A superadmin never pays for that second question. The session is only read
 * when the user record alone says no, which is also the only case where the
 * answer can change the outcome. A session we cannot identify at all is treated
 * the same as a customer's: not staff.
 *
 * None of this is the security boundary. The API refuses those requests on its
 * own; this is what stops the console assembling itself around requests that
 * are all going to fail.
 */
export async function requireStaff(queryClient: QueryClient): Promise<void> {
  const user = queryClient.getQueryData<CurrentUser>(['currentUser']);

  if (user?.role === 'superadmin') {
    return;
  }

  const impersonatedBy = await queryClient.ensureQueryData({
    queryKey: ['impersonatedBy'],
    queryFn: fetchImpersonatedBy,
  });

  if (impersonatedBy) {
    return;
  }

  /*
   * Another origin, so it is a navigation out of the app rather than a route
   * change — thrown rather than performed, rejecting this load the way every
   * other guard verdict does.
   */
  throw redirect({ href: customerAppUrl() });
}

/**
 * Whether this session may reach the pages that only make sense from inside
 * Wundo — the `/admin` tools. Superadmin is the door; an internal organisation
 * is the inner door.
 */
export function isInternalOrganisation(
  user: Pick<CurrentUser, 'organisation'> | undefined,
): boolean {
  return user?.organisation?.type === 'internal';
}

/**
 * The inner door: the pages that only make sense from inside Wundo.
 *
 * Every route the Vue router marked `requiresSuperadminInternal` calls this
 * from its `beforeLoad`. Somebody who does not belong there is sent to the
 * console's own home page rather than refused — they are signed in and staff,
 * just not ours, and there is a console for them to be in.
 */
export function requireInternalOrganisation(queryClient: QueryClient): void {
  const user = queryClient.getQueryData<CurrentUser>(['currentUser']);

  if (!isInternalOrganisation(user)) {
    throw redirect({ to: '/' });
  }
}
